<template>
    <div class="modal-overlay" ref="panel">
        <div class="modal-content max-width-800">
            <div class="panel-title">
                <div>
                    <i :class="getFeatureIcons('competitors')"></i>
                    <span> {{ getFeatureNames('competitors') }} - {{ this.symbol }}</span>
                </div>
                <span @click="closePanel"><i class="cursor-pointer ti-close right-side-toggle medium-icon"></i></span>
            </div>
            <div class="card card-body modal-body mt-2">
                <ul class="nav nav-tabs customtab" role="tablist">
                    <li class="nav-item">
                        <a class="nav-link cursor-pointer" :class="{ active: this.viewType === 'list' }"
                            @click="() => onSelectedTab('list')">
                            <i class="fas fa-list"></i>
                        </a>
                    </li>
                    <li class="nav-item">
                        <a class="nav-link cursor-pointer" :class="{ active: this.viewType === 'line' }"
                            @click="() => onSelectedTab('line')">
                            <i class="fas fa-chart-line"></i>
                        </a>
                    </li>
                    <li class="nav-item">
                        <a class="nav-link cursor-pointer" :class="{ active: this.viewType === 'bubble' }"
                            @click="() => onSelectedTab('bubble')">
                            <i class="far fa-circle"></i>
                        </a>
                    </li>
                    <li class="nav-item">
                        <a class="nav-link cursor-pointer" :class="{ active: this.viewType === 'inventory' }"
                            @click="() => onSelectedTab('inventory')">
                            <i class="fas fa-warehouse"></i>
                        </a>
                    </li>
                </ul>
                <div class="tab-content mt-2">
                    <div class="tab-pane" :class="{ active: this.viewType === 'list' }">
                        <loading-container :loading="form.competitorsListLoading">
                            <table class="table table-bordered compare-stock-table sticky-table">
                                <thead>
                                    <tr vertical-align="baseline">
                                        <th style="vertical-align: top">{{ $t(`stocks.information`) }}</th>
                                        <th class="th-sort"
                                            :class="(sort.field === 'roe' && sort.order) ? (sort.order === 'desc' ? 'descending' : 'ascending') : ''"
                                            @click="onSortField('roe')">
                                            {{ $t('stocks.roe') }}
                                        </th>
                                        <th class="th-sort"
                                            :class="(sort.field === 'pe' && sort.order) ? (sort.order === 'desc' ? 'descending' : 'ascending') : ''"
                                            @click="onSortField('pe')">
                                            {{ $t('stocks.current_pe') }}
                                        </th>
                                        <th class="th-sort"
                                            :class="(sort.field === 'dividend' && sort.order) ? (sort.order === 'desc' ? 'descending' : 'ascending') : ''"
                                            @click="onSortField('dividend')">
                                            {{ $t('dividend.title') }}
                                        </th>
                                        <th class="th-sort"
                                            :class="(sort.field === 'mos' && sort.order) ? (sort.order === 'desc' ? 'descending' : 'ascending') : ''"
                                            @click="onSortField('mos')">
                                            {{ $t('stocks.mos.title') }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in competitors" :key="index"
                                        :class="index === 0 ? 'bg-gray' : ''">
                                        <td :class="index === 0 ? 'font-bold' : ''">
                                            <div>
                                                <span class="cursor-pointer text-info"
                                                    @click="() => linkToStockInfo(item.code, true)">
                                                    <span class="mytooltip tooltip-effect-5">
                                                        {{ item.code }}
                                                        <span class="tooltip-content clearfix">
                                                            <span class="tooltip-text p-t-10">{{ item.name }}</span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </div>
                                            <div>
                                                {{ $t('stocks.debt_to_equity') }}:
                                                <span class="text-info" :class="{
                                                    'text-purple font-bold': item.mvp.debt_to_equity
                                                }">
                                                    {{ item.debt_to_equity | numberDisplayFloat(2) }}
                                                    <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                                                        <small>#{{ item.rank_no.debt_to_equity }}</small>
                                                        <span class="tooltip-content clearfix">
                                                            <span class="tooltip-text p-t-10">Top {{
                                                                item.rank_no.debt_to_equity
                                                                }} {{
                                                                $t('stocks.debt_to_equity') }}</span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </div>
                                            <div>
                                                {{ $t('stocks.current_pb') }}:
                                                <span class="text-info" :class="{
                                                    'text-purple font-bold': item.mvp.pb
                                                }">
                                                    {{ item.pb | numberDisplayFloat(3) }}
                                                    <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                                                        <small>#{{ item.rank_no.pb }}</small>
                                                        <span class="tooltip-content clearfix">
                                                            <span class="tooltip-text p-t-10">Top {{ item.rank_no.pb }}
                                                                {{
                                                                $t('stocks.current_pb')
                                                                }}</span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </div>

                                            <div>
                                                {{ $t('stocks.ps') }}:
                                                <span class="text-info" :class="{
                                                    'text-purple font-bold': item.mvp.ps
                                                }">
                                                    {{ item.ps | numberDisplayFloat(3) }}
                                                    <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                                                        <small>#{{ item.rank_no.ps }}</small>
                                                        <span class="tooltip-content clearfix">
                                                            <span class="tooltip-text p-t-10">Top {{ item.rank_no.ps }}
                                                                {{
                                                                $t('stocks.ps') }}</span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </div>
                                        </td>
                                        <td :class="{
                                            'font-bold': index === 0,
                                            'text-purple font-bold': item.mvp.roe
                                        }">
                                            {{ item.roe | numberDisplayFloat(3) }}
                                            <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                                                <small>#{{ item.rank_no.roe }}</small>
                                                <span class="tooltip-content clearfix">
                                                    <span class="tooltip-text p-t-10">Top {{ item.rank_no.roe }} {{
                                                        $t('stocks.roe') }}</span>
                                                </span>
                                            </span>
                                        </td>
                                        <td :class="{
                                            'font-bold': index === 0,
                                            'text-purple font-bold': item.mvp.pe
                                        }">
                                            {{ item.pe | numberDisplayFloat(3) }}
                                            <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                                                <small>#{{ item.rank_no.pe }}</small>
                                                <span class="tooltip-content clearfix">
                                                    <span class="tooltip-text p-t-10">Top {{ item.rank_no.pe }} {{
                                                        $t('stocks.current_pe') }}</span>
                                                </span>
                                            </span>
                                        </td>
                                        <td :class="{
                                            'font-bold': index === 0,
                                            'text-purple font-bold': item.mvp.dividend
                                        }">
                                            {{ item.dividend | percentDisplay() }}
                                            <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                                                <small>#{{ item.rank_no.dividend }}</small>
                                                <span class="tooltip-content clearfix">
                                                    <span class="tooltip-text p-t-10">Top {{ item.rank_no.dividend }} {{
                                                        $t('dividend.title')
                                                        }}</span>
                                                </span>
                                            </span>
                                        </td>

                                        <td :class="{
                                            'font-bold': index === 0,
                                            'text-purple font-bold': item.mvp.mos
                                        }">
                                            {{ valueFilter(item.mos) }}
                                            <span class="mytooltip tooltip-effect-5" v-if="index === 0">
                                                <small>#{{ item.rank_no.mos }}</small>
                                                <span class="tooltip-content clearfix">
                                                    <span class="tooltip-text p-t-10">Top {{ item.rank_no.mos }} {{
                                                        $t('stocks.mos.title') }}</span>
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </loading-container>
                    </div>
                    <div class="tab-pane" :class="{ active: this.viewType === 'line' }">
                        <el-select class="w-100 col-md-5 col-sm-5 mt-2" v-model="periodIndustry" clearable
                            @change="fetchIndustryCharts('line')">
                            <el-option v-for="item in periodOptions" :key="item.code" :label="item.name"
                                :value="item.code">
                                <span>{{ item.name }}</span>
                            </el-option>
                        </el-select>
                        <div v-if="lineCompetitors">
                            <loading-container :loading="form.lineChartCompetitorsLoading">
                                <div class="align-items-center m-t-20 m-b-20 p-positive">
                                    <div id="lineCompetitorsChart">
                                        <apexchart type="line" height="350" :options="lineCompetitors.chartOptions"
                                            :series="lineCompetitors.series" />
                                    </div>
                                </div>
                            </loading-container>
                        </div>
                    </div>
                    <div class="tab-pane" :class="{ active: this.viewType === 'bubble' }">
                        <div v-if="bubbleCompetitors">
                            <loading-container :loading="form.bubbleChartCompetitorsLoading">
                                <div class="align-items-center m-t-20 m-b-20 p-positive">
                                    <div id="bubbleCompetitorsChart">
                                        <apexchart type="bubble" height="350" :options="bubbleCompetitors.chartOptions"
                                            :series="bubbleCompetitors.series" />
                                    </div>
                                </div>
                            </loading-container>
                        </div>
                    </div>
                    <div class="tab-pane" :class="{ active: this.viewType === 'inventory' }">
                        <div v-if="inventories">
                            <el-select class="w-100 col-md-5 col-sm-5 mt-2" v-model="indicator" clearable
                                @change="fetchIndicatorLineChart()">
                                <el-option v-for="item in statementOptions" :key="item.code" :label="item.name"
                                    :value="item.code">
                                    <span>{{ item.name }}</span>
                                </el-option>
                            </el-select>
                            <loading-container :loading="form.lineInventoriesLoading">
                                <div class="align-items-center m-t-20 m-b-20 p-positive">
                                    <div id="lineInventoriesChart">
                                        <apexchart type="line" height="350" :options="inventories.chartOptions"
                                            :series="inventories.series" />
                                    </div>
                                </div>
                            </loading-container>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import LoadingContainer from "@/components/Misc/LoadingContainer";
import common from "@/libs/common";
import FormButton from '@/components/Form/FormButton';
import Breadcrumb from "@/components/Common/Breadcrumb";

export default {
    components: {
        Breadcrumb,
        LoadingContainer,
        FormButton
    },
    props: {
        symbol: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            viewType: 'list',
            periodIndustry: 'annual',
            indicator: 'inventory'
        };
    },
    beforeMount() {
        this.getCompetitors(this.symbol);
    },
    computed: {
        ...mapGetters({
            form: "industry/form",
            sort: "industry/sort",
            inventories: "industry/inventories",
            competitors: "industry/competitors",
            lineCompetitors: "industry/lineCompetitors",
            bubbleCompetitors: "industry/bubbleCompetitors"
        }),
        periodOptions() {
            return common.getPeriodOptions();
        },
        statementOptions() {
            return common.getStatementOptions();
        }
    },
    methods: {
        ...mapActions({
            setDefaultCode: "configuration/setDefaultCode",
            getCompetitors: "industry/getCompetitors",
            setSortField: "industry/setSortField",
            getIndicatorLineChart: "industry/getIndicatorLineChart",
            getLineChartCompetitors: "industry/getLineChartCompetitors",
            getBubbleChartCompetitors: "industry/getBubbleChartCompetitors"
        }),
        onSelectedTab(type) {
            this.viewType = type;
            switch (type) {
                case 'line':
                    this.getLineChartCompetitors({ 'symbol': this.symbol, 'period': this.periodIndustry });
                    break;
                case 'bubble':
                    this.getBubbleChartCompetitors(this.symbol);
                    break;
                case 'inventory':
                    this.getIndicatorLineChart({'symbol' : this.symbol, 'indicator' : this.indicator});
                    break;
                default:
                    this.getCompetitors(this.symbol);
                    break;
            }
        },
        fetchIndustryCharts() {
            if (this.symbol) {
                if (this.periodIndustry) {
                    this.getLineChartCompetitors({ 'symbol': this.symbol, 'period': this.periodIndustry });
                }
            }
        },
        fetchIndicatorLineChart() {
            if (this.symbol && this.indicator) {
                this.getIndicatorLineChart({'symbol' : this.symbol, 'indicator' : this.indicator});
            }
        },
        getFeatureIcons(name) {
            return common.getFeatureIcons(name);
        },
        getFeatureNames(name) {
            return common.getFeatureNames(name);
        },
        valueFilter(value, isDca) {
            if (isDca) {
                return _.round(value, 1);
            }
            return _.round(value * 100, 1) + '%';
        },
        linkToStockInfo(code, isNewtab) {
            this.setDefaultCode(code);
            if (isNewtab) {
                let routeData = this.$router.resolve({ name: 'overview', params: { codeId: code } });
                window.open(routeData.href, '_blank');
            } else {
                this.$router.push({ name: 'overview', params: { codeId: code } }).catch(() => { });
            }
        },
        onSortField(sortField) {
            if (sortField === this.sort.field) {
                this.setSortField({ key: 'order', value: this.sort.order === 'asc' ? 'desc' : 'asc' });
            }
            this.setSortField({ key: 'field', value: sortField });
            this.getCompetitors(this.symbol);
        },
        closePanel() {
            this.$emit('close');
        }
    },
}
</script>

<style>
.header-row {
    display: flex;
    gap: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.th-sort {
    position: relative;
    vertical-align: top;
    cursor: pointer;
}

.descending:after {
    content: "\2193";
}

.ascending:after {
    content: "\2191";
}

.ascending:after,
.descending:after {
    display: inline-block;
}

@media only screen and (max-width: 768px) {

    table.compare-stock-table th,
    table.compare-stock-table td {
        font-size: 12px;
        padding: 10px;
    }
}

@media only screen and (max-width: 500px) {

    table.compare-stock-table th,
    table.compare-stock-table td {
        font-size: 10px;
        padding: 2px;
    }
}
</style>